/**
 * AdminAITemplateFactory.jsx
 *
 * AI Template Factory — Admin Portal console for the factory at
 * src/aiTemplateFactory/. Runs the full production line client-side:
 *
 *   variation → DNA → duplicate detection → spec synthesis
 *     → AI Design Engine → quality filter (≥ 90) → image queries
 *
 * and shows each accepted template's DNA, score, image queries, and the
 * complete DesignSpec JSON. No network calls, no rendering, no OpenAI yet —
 * the factory's `overrides` parameter is where OpenAI will plug in.
 */
import React, { useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Factory, Play, Copy, CheckCircle2, XCircle, Dna, Image as ImageIcon,
  Trash2, ChevronDown, ChevronUp, Gauge, History,
} from 'lucide-react';
import {
  manufacture,
  factoryHistory,
  listCategories,
} from '../../aiTemplateFactory';

const GENE_CHIP_ORDER = [
  'layout', 'heroPosition', 'paletteTheme', 'harmony', 'typographyTheme',
  'typeScale', 'spacing', 'decorationPack', 'offerStyle', 'footerStyle',
  'imageCount', 'imageStyle',
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600 mb-1 block">{label}</span>
    {children}
  </label>
);

const SelectBox = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const TemplateCard = ({ template, index }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copySpec = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(template.designSpec, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — ignore */ }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Dna className="w-4 h-4 text-purple-500" /> Template #{index + 1}
        </h3>
        <span className={`text-lg font-bold ${template.qualityScore >= 95 ? 'text-emerald-600' : 'text-amber-600'}`}>
          {template.qualityScore}<span className="text-xs text-gray-400">/100</span>
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {GENE_CHIP_ORDER.map((gene) => (
          <span key={gene} className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 px-2 py-0.5 text-[11px]"
            title={gene}>
            {gene}: <b className="ml-1">{`${template.dna[gene]}`}</b>
          </span>
        ))}
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] border border-gray-200 text-gray-600"
          title={`seed ${template.dna.seed}${template.dna.dark ? ' · dark' : ''}`}
        >
          <span className="w-3 h-3 rounded-full mr-1 border border-gray-300" style={{ background: template.dna.seed }} />
          {template.dna.seed}{template.dna.dark ? ' · dark' : ''}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <ImageIcon className="w-3.5 h-3.5 text-gray-400" />
        {template.imageQueries.map((q) => (
          <span key={q} className="rounded bg-gray-100 text-gray-600 px-1.5 py-0.5 text-[11px]">{q}</span>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
          {open ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
          DesignSpec
        </Button>
        <Button variant="outline" size="sm" onClick={copySpec}>
          {copied ? <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />}
          {copied ? 'Copied' : 'Copy JSON'}
        </Button>
        <span className="ml-auto text-[11px] text-gray-400 truncate max-w-[180px]" title={template.fingerprint}>
          {template.fingerprint.slice(0, 28)}…
        </span>
      </div>

      {open && (
        <pre className="mt-3 bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-auto max-h-72 leading-relaxed">
          {JSON.stringify(template.designSpec, null, 2)}
        </pre>
      )}
    </div>
  );
};

const AdminAITemplateFactory = () => {
  const [category, setCategory] = useState('restaurant');
  const [count, setCount] = useState(4);
  const [minScore, setMinScore] = useState(90);
  const [similarity, setSimilarity] = useState(70);
  const [rngSeed, setRngSeed] = useState('');

  const [batch, setBatch] = useState(null);
  const [error, setError] = useState('');
  const [historySize, setHistorySize] = useState(factoryHistory.size);

  const categories = useMemo(listCategories, []);

  const runFactory = () => {
    setError('');
    try {
      const result = manufacture({
        category,
        count: Math.max(1, Math.min(20, Number(count) || 1)),
        minScore: Number(minScore) || 90,
        similarityThreshold: (Number(similarity) || 70) / 100,
        seed: rngSeed.trim() ? Number(rngSeed) : Date.now(),
      });
      setBatch(result);
      setHistorySize(factoryHistory.size);
    } catch (e) {
      setBatch(null);
      setError(e.message);
    }
  };

  const clearHistory = () => {
    factoryHistory.clear();
    setHistorySize(0);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Factory className="w-6 h-6 text-purple-600" /> AI Template Factory
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Mass-produces unique DesignSpecs for the AI Design Engine — DNA-deduplicated,
              quality-gated at {minScore}+. OpenAI will steer this later via gene overrides.
            </p>
          </div>
          <Button onClick={runFactory} className="bg-purple-600 hover:bg-purple-700">
            <Play className="w-4 h-4 mr-2" /> Run Factory
          </Button>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Field label="Category">
              <SelectBox value={category} onChange={setCategory}
                options={categories.map((c) => ({ value: c.id, label: c.name }))} />
            </Field>
            <Field label="Batch size">
              <Input type="number" min={1} max={20} value={count}
                onChange={(e) => setCount(e.target.value)} className="h-9 text-sm" />
            </Field>
            <Field label="Min quality score">
              <Input type="number" min={0} max={100} value={minScore}
                onChange={(e) => setMinScore(e.target.value)} className="h-9 text-sm" />
            </Field>
            <Field label="Similarity limit (%)">
              <Input type="number" min={0} max={100} value={similarity}
                onChange={(e) => setSimilarity(e.target.value)} className="h-9 text-sm" />
            </Field>
            <Field label="RNG seed (blank = random)">
              <Input value={rngSeed} placeholder="e.g. 1234"
                onChange={(e) => setRngSeed(e.target.value)} className="h-9 text-sm" />
            </Field>
          </div>
          <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
            <History className="w-3.5 h-3.5" />
            <span><b className="text-gray-700">{historySize}</b> templates in generation history (deduplicated against every run)</span>
            <Button variant="ghost" size="sm" className="ml-auto text-red-600 hover:text-red-700 h-7"
              onClick={clearHistory}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear history
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2 text-sm text-red-700 mb-6">
            <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}

        {!batch && !error && (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-16 text-center text-gray-400">
            <Factory className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Pick a category and hit <b>Run Factory</b>.</p>
            <p className="text-xs mt-1">Every accepted template is unique DNA scoring {minScore}+ — rejects are retried automatically.</p>
          </div>
        )}

        {batch && (
          <>
            {/* Stats */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-6 text-sm">
              <span className="flex items-center gap-1.5 font-medium text-gray-900">
                <Gauge className="w-4 h-4 text-purple-500" />
                {batch.stats.accepted} accepted
              </span>
              <span className="text-gray-500">{batch.stats.attempts} attempts</span>
              <span className="text-amber-600">{batch.stats.rejectedQuality} rejected on quality</span>
              <span className="text-blue-600">{batch.stats.rejectedDuplicate} rejected as duplicates</span>
              {!batch.complete && (
                <span className="text-red-600 font-medium">
                  Batch incomplete — attempt budget hit. Lower the score bar or clear history.
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {batch.templates.map((t, i) => (
                <TemplateCard key={t.fingerprint} template={t} index={i} />
              ))}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAITemplateFactory;
